import { useState } from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { useNavigate, useLocation } from 'react-router-dom'
import { gql } from 'graphql-request'

import { ClockIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import FlashMessage, { useFlashMessage } from '@components/FlashMessage'
import { useMutation } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'

import SelectField from './SelectField'

const UPDATE_RESEARCH_CONSENT_MUTATION = gql`
  mutation updateResearchConsent($input: UpdateResearchConsentInput!) {
    updateResearchConsent(input: $input) {
      classroomEnrollment {
        researchConsent
      }
    }
  }
`

const ConsentFlow = ({ classroom }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setFlashMessage } = useFlashMessage()
  const { currentMembership: { organization: { name } = {} } = {} } = useCurrentUser()

  const [step, setStep] = useState(0)
  const nextStep = () => setStep(step + 1)
  const previousStep = () => setStep(step - 1)

  const [researchConsent, setResearchConsent] = useState(null)

  const { mutate: updateResearchConsent, isLoading: isUpdatingResearchConsent } = useMutation({
    gqlMutation: UPDATE_RESEARCH_CONSENT_MUTATION,
    variables: { input: { classroomId: classroom.id, researchConsent } },
    onError: () => {
      setFlashMessage('Oops, something went wrong! Try again?', 'error')
    },
    onSuccess: () => {
      if (!researchConsent && classroom.researchConsentingOnly) {
        navigate('blocked')
        return
      }

      setStep(3)
    }
  })

  const finishConsenting = () => {
    if (!researchConsent && classroom.researchConsentingOnly) {
      navigate('blocked')
      return
    }

    const redirectTo = location.search.split('redirect_to=')[1]
    if (redirectTo) {
      navigate(redirectTo)
    } else {
      navigate(`/students/classrooms/${classroom.id}`)
    }
  }

  return (
    <div className='bg-white md:h-screen p-5 pt-24 md:p-2 h-min-screen w-full flex flex-col items-center justify-center overflow-hidden'>

      <FlashMessage />

      <SwitchTransition mode='out-in'>
        <CSSTransition
          key={step}
          timeout={50}
          classNames={{
            enter: 'opacity-0',
            enterActive: 'opacity-100 transition-opacity duration-500 ease-out',
            exit: 'opacity-100',
            exitActive: 'opacity-0 transition-opacity duration-500 ease-out'
          }}
          unmountOnExit
        >
          <div>
            <Choose>
              <When condition={step === 0}>
                <div className='max-w-md'>
                  <div className='mb-6'>
                    <span className='text-mirage-950 font-light bg-mirage-50 rounded-lg py-2 px-2'>{classroom.name}</span>
                  </div>

                  <h1 className='text-mirage-950 text-3xl font-semibold my-3'>
                    This classroom is part of a research study.
                  </h1>
                  <p className='text-lg font-light'>
                    Before you continue, please review and provide your consent regarding the use of your data for this study.
                  </p>

                  <div className='my-6 flex gap-4 items-center'>
                    <button
                      onClick={nextStep}
                      className='bg-primary text-primary-foreground text-md rounded-lg py-2 px-5 transition-opacity duration-500 ease-in-out disabled:opacity-20'
                    >
                      Continue
                    </button>
                    <div className='text-primary flex gap-2'>
                      <ClockIcon className='w-5 h-5' />
                      <span>1 minute</span>
                    </div>
                  </div>
                </div>
              </When>

              <When condition={step === 1}>
                <div className='max-w-md'>
                  <p className='text-lg font-light mb-5 leading-snug'>
                    {name} is running a research study using Mindjoy's Al tutoring platform. Your participation is voluntary.
                  </p>
                  <p className='text-lg font-light mb-5 leading-snug'>
                    If you consent, your data relating to this classroom may be used as part of research. Your name, email address and chat history will be visible to lecturers. Any published reports will aggregate and anonymise your data.
                  </p>
                  <p className='text-lg font-light mb-5 leading-snug'>
                    Do not send any messages containing personal information such as your name, surname, email, phone number, identity number etc.
                  </p>
                  <div className='my-6 flex flex-col md:flex-row gap-4 items-center'>
                    <a
                      href={classroom.researchMotivationDocument?.url || '#'}
                      target='_blank'
                      rel='noopener noreferrer'
                      className={`w-full md:w-auto rounded-lg py-2 px-5 text-mirage-950 text-md border border-[#E2E8F0] ${!classroom.researchMotivationDocument?.url ? 'pointer-events-none opacity-50' : ''}`}
                    >
                      <div className='text-mirage-950 flex gap-2 justify-center md:justify-start'>
                        <ArrowTopRightOnSquareIcon className='w-5 h-5' />
                        <span>Full research motivation</span>
                      </div>
                    </a>
                    <button
                      onClick={nextStep}
                      className='w-full md:w-auto bg-mirage-950 text-white text-md rounded-lg py-2 px-5 shadow-[0_2.5px_32px_0_rgba(0,0,64,0.1)] transition-opacity duration-500 ease-in-out disabled:opacity-20'
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </When>

              <When condition={step === 2}>
                <SelectField
                  value={researchConsent}
                  setValue={setResearchConsent}
                  nextStep={() => updateResearchConsent()}
                  isBusy={isUpdatingResearchConsent}
                  previousStep={previousStep}
                  title='I agree to participate in this study and understand that my data may be used for research purposes.'
                  options={[
                    {
                      emoji: '✅',
                      title: 'I consent',
                      value: true
                    },
                    {
                      emoji: '❌',
                      title: "I don't want to participate",
                      value: false
                    }
                  ]}
                />
              </When>

              <When condition={step === 3}>
                <div className='max-w-sm text-center'>

                  <h1 className='text-mirage-950 text-3xl font-semibold my-3'>
                    <Choose>
                      <When condition={researchConsent}>
                        Thank you for participating in this study!
                      </When>
                      <When condition={!researchConsent}>
                        Your data will not be used for this study.
                      </When>
                    </Choose>
                  </h1>

                  <div className='mt-6 flex gap-4 items-center justify-center'>
                    <button
                      onClick={finishConsenting}
                      className='bg-mirage-950 text-white text-md rounded-lg py-2 px-5 shadow-[0_2.5px_32px_0_rgba(0,0,64,0.1)] transition-opacity duration-500 ease-in-out disabled:opacity-20'
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </When>
            </Choose>
          </div>
        </CSSTransition>
      </SwitchTransition>
    </div>
  )
}

export default ConsentFlow
